import React, { useState, useRef } from 'react';
import { ArrowRight, Brain, Users, Code, Network, Terminal, Copy, Check, Lightbulb, Building2, Calendar } from "lucide-react";
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';

import { Button } from "../components/ui/button";
import Shuffle from './ShuffleText';
import Navbar from '../components/Navbar';
import LiquidEther from '../components/LiquidEther';
import Stack from '../components/Stack';
import Team1 from '../components/Team1';
import { Footer2 } from '../components/Footer2';

const Hero34 = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });

  // Parallax transforms for different elements
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const yFast = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const ySlow = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.8, 0.3]);

  return (
    <section ref={ref} className="min-h-screen relative overflow-hidden">
      {/* Parallax Background Layers */}
      <motion.div 
        style={{ y: yFast, scale }}
        className="absolute inset-0 z-0 pointer-events-none"
      >
        <LiquidEther
          colors={['#525253', '#0993f8', '#4ea8f4' ]} // Vibrant blue and white
          mouseForce={50}
          cursorSize={150}
          resolution={0.6}
          autoDemo={true}
          autoSpeed={0.3}
          autoIntensity={1.8}
          className="w-full h-full"
        />
      </motion.div>

      {/* Floating Parallax Elements */}
      <motion.div 
        style={{ y: ySlow }}
        className="absolute top-16 lg:top-20 right-16 lg:right-20 w-24 h-24 lg:w-32 lg:h-32 bg-white/10 rounded-full blur-2xl z-1"
      />
      <motion.div 
        style={{ y: y }}
        className="absolute bottom-32 lg:bottom-40 left-16 lg:left-20 w-20 h-20 lg:w-24 lg:h-24 bg-[#0395fc]/20 rounded-full blur-xl z-1"
      />
      <motion.div 
        style={{ y: yFast }}
        className="absolute top-1/2 right-1/4 w-12 h-12 lg:w-16 lg:h-16 bg-yellow-300/30 rounded-full blur-lg z-1"
      />
      
      {/* Additional minimalistic background elements */}
      <motion.div 
        style={{ y: ySlow }}
        className="absolute top-1/4 left-1/4 w-16 h-16 lg:w-20 lg:h-20 bg-[#0395fc]/10 rounded-full blur-xl z-1"
        animate={{ 
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.6, 0.3]
        }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />
      <motion.div 
        style={{ y: y }}
        className="absolute bottom-1/4 right-1/4 w-20 h-20 lg:w-28 lg:h-28 bg-white/5 rounded-full blur-2xl z-1"
        animate={{ 
          scale: [1.1, 0.9, 1.1],
          opacity: [0.2, 0.5, 0.2]
        }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />
      <motion.div 
        style={{ y: yFast }}
        className="absolute top-3/4 left-1/2 w-10 h-10 lg:w-12 lg:h-12 bg-[#4ea8f4]/15 rounded-full blur-lg z-1"
        animate={{ 
          scale: [0.8, 1.3, 0.8],
          opacity: [0.4, 0.7, 0.4]
        }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
      />
      <motion.div 
        style={{ y: ySlow }}
        className="absolute top-1/6 right-1/2 w-14 h-14 lg:w-18 lg:h-18 bg-[#0993f8]/12 rounded-full blur-xl z-1"
        animate={{ 
          scale: [1.2, 0.8, 1.2],
          opacity: [0.25, 0.55, 0.25]
        }}
        transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 3 }}
      />
      <motion.div 
        style={{ y: y }}
        className="absolute bottom-1/6 left-3/4 w-12 h-12 lg:w-14 lg:h-14 bg-yellow-300/20 rounded-full blur-lg z-1"
        animate={{ 
          scale: [0.9, 1.4, 0.9],
          opacity: [0.35, 0.65, 0.35]
        }}
        transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
      />
      
      {/* Content with opacity fade */}
      <motion.div 
        style={{ opacity }}
        className="container mx-auto px-4 py-16 relative z-10"
      >
        <div className="grid items-center gap-8 md:gap-10 lg:gap-12 md:grid-cols-2 min-h-[70vh] sm:min-h-[80vh] lg:min-h-[80vh]">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="flex flex-col items-center text-center md:items-start md:text-left space-y-6 lg:space-y-8"
          >
          
            {/* Main Heading */}
            <div className="space-y-3 lg:space-y-4">
              <div className="space-y-1 lg:space-y-2">
                <Shuffle 
                  text="DALHOUSIE "
                  tag="h1"
                  className="text-4xl sm:text-5xl md:text-[3.5rem] lg:text-6xl xl:text-7xl font-black font-[var(--font-anton)] leading-tight text-foreground"
                  textAlign="left"
                  duration={0.5}
                  stagger={0.05}
                  scrambleCharset="ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*"
                  shuffleTimes={3}
                  threshold={0.3}
                  style={{ fontFamily: 'var(--font-anton)' }}
                />
                <Shuffle 
                  text="MACHINE "
                  tag="h1"
                  className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black font-[var(--font-anton)] leading-tight"
                  textAlign="left"
                  duration={0.5}
                  stagger={0.05}
                  scrambleCharset="ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*"
                  shuffleTimes={3}
                  threshold={0.3}
                  maxDelay={0.2}
                  style={{ fontFamily: 'var(--font-anton)', color: '#0395fc' }}
                />
                <Shuffle 
                  text="LEARNING "
                  tag="h1"
                  className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black font-[var(--font-anton)] leading-tight"
                  textAlign="left"
                  duration={0.5}
                  stagger={0.05}
                  scrambleCharset="ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*"
                  shuffleTimes={3}
                  threshold={0.3}
                  maxDelay={0.4}
                  style={{ fontFamily: 'var(--font-anton)', color: '#0395fc' }}
                />
                <Shuffle 
                  text="SOCIETY "
                  tag="h1"
                  className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black font-[var(--font-anton)] leading-tight"
                  textAlign="left"
                  duration={0.5}
                  stagger={0.05}
                  scrambleCharset="ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*"
                  shuffleTimes={3}
                  threshold={0.3}
                  maxDelay={0.6}
                  style={{ fontFamily: 'var(--font-anton)', color: '#0395fc' }}
                />
              </div>
              <div className="h-1.5 w-24 sm:h-2 sm:w-32 bg-[#0395fc] transform -skew-x-12 mx-auto lg:mx-0"></div>
            </div>
            
            {/* Subtitle */}
            <p className="text-lg sm:text-xl lg:text-l text-muted-foreground max-w-1xl font-[var(--font-dm-sans)] leading-relaxed px-4 lg:px-0">
              Empowering the ML Community at Dalhousie University
            </p>
            
            {/* CTA Buttons */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-3 lg:gap-4 w-full lg:w-auto px-4 lg:px-0"
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  size="lg" 
                  className="text-base lg:text-lg font-black bg-[#0395fc] border-[#0395fc] hover:bg-[#0070c0] hover:border-[#0070c0] text-white w-full sm:w-auto"
                  onClick={() => window.open('https://discord.com/invite/47Pyqyhnuu?fbclid=PAZXh0bgNhZW0CMTEAAaf3CY6keaV8UQoHI_ZnlsAlax2Ea_2Sz1LFNBZN6arK2OVmRWSanx7MTnYH_A_aem_fCfe5I9D6Z0mA2c8PfV6cg', '_blank')}
                >
                  JOIN COMMUNITY
                  <ArrowRight className="size-4 lg:size-5" />
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/events">
                  <Button variant="outline" size="lg" className="text-base lg:text-lg font-black w-full sm:w-auto">
                    <Users className="size-4 lg:size-5" />
                    VIEW EVENTS
                  </Button>
                </Link>
              </motion.div>
            </motion.div>
            
            {/* Stats */}
            {/*
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="grid grid-cols-3 gap-4 lg:gap-8 pt-6 lg:pt-8 w-full max-w-sm lg:max-w-lg mx-auto lg:mx-0 px-4 lg:px-0"
            >
              <motion.div 
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                whileHover={{ scale: 1.1 }}
                className="text-center lg:text-left cursor-pointer"
              >
                <motion.div 
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  className="text-2xl sm:text-3xl lg:text-4xl font-black font-[var(--font-anton)]" 
                  style={{ color: '#0395fc' }}
                >
                  100+
                </motion.div>
                <div className="text-xs sm:text-sm font-bold text-muted-foreground uppercase tracking-wider">Members</div>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 1.0 }}
                whileHover={{ scale: 1.1 }}
                className="text-center lg:text-left cursor-pointer"
              >
                <motion.div 
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                  className="text-2xl sm:text-3xl lg:text-4xl font-black text-accent font-[var(--font-anton)]"
                >
                  25+
                </motion.div>
                <div className="text-xs sm:text-sm font-bold text-muted-foreground uppercase tracking-wider">Workshops</div>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 1.2 }}
                whileHover={{ scale: 1.1 }}
                className="text-center lg:text-left cursor-pointer"
              >
                <motion.div 
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  className="text-2xl sm:text-3xl lg:text-4xl font-black font-[var(--font-anton)]" 
                  style={{ color: '#0395fc' }}
                >
                  10+
                </motion.div>
                <div className="text-xs sm:text-sm font-bold text-muted-foreground uppercase tracking-wider">Projects</div>
              </motion.div>
            </motion.div>
            */}
          </motion.div>
          
          {/* Hero Visual - PhotoStack */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
            className="relative flex flex-col justify-center items-center w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto"
          >
            {/* Swipe Prompt Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
              animate={{ opacity: 1, scale: 1, rotate: -3 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-white rounded-full border-2 border-[#0395fc] shadow-lg w-fit mb-4 transform -rotate-3 hover:rotate-0 transition-transform duration-300"
            >
              <svg className="w-4 h-4 text-[#0395fc] animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
              <span className="font-bold text-sm font-[var(--font-dm-sans)] text-[#0395fc] italic">
                Swipe to explore
              </span>
              <svg className="w-4 h-4 text-[#0395fc] animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
            </motion.div>

            <div className="w-full aspect-[4/3] sm:aspect-[5/4] md:aspect-[3/2] lg:aspect-[29/20]">
              <Stack
                randomRotation={true}
                sensitivity={180}
                sendToBackOnClick={false}
                cardDimensions={{
                  width: '100%',
                  height: '100%'
                }}
                cardsData={[
                  { id: 1, img: "/1.jpg" }, // AI/ML visualization
                  { id: 2, img: "/2.jpg" }, // Data science
                  { id: 3, img: "/3.jpg" }, // Neural networks
                  { id: 4, img: "/4.jpg" }, // Technology/coding
                  { id: 5, img: "/Pizza.jpg" },
                  { id: 6, img: "/7.jpg" }, // New image
                ]}
                animationConfig={{ stiffness: 200, damping: 20 }}
              />
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export { Hero34 };

// About Us Section Component
const AboutUsSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.1 });
  
  // Parallax effect for this section
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  const yParallax = useTransform(scrollYProgress, [0, 1], ["20%", "-20%"]);
  const scaleParallax = useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 1, 0.9]);
  const rotateParallax = useTransform(scrollYProgress, [0, 1], [-2, 2]);

  return (
    <section id="aboutUSSection" ref={sectionRef} className="py-32 bg-transparent relative overflow-hidden">
      {/* Parallax background elements */}
      <motion.div 
        style={{ y: yParallax }}
        className="absolute top-0 right-0 w-80 h-80 bg-[#0395fc]/5 rounded-full blur-3xl pointer-events-none"
      />
      <motion.div 
        style={{ y: yParallax, rotate: rotateParallax }}
        className="absolute bottom-20 left-10 w-32 h-32 bg-yellow-300/20 rounded-lg blur-xl pointer-events-none transform"
      />
      
      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <div className="grid gap-12 lg:gap-16 lg:grid-cols-2 items-center">
          {/* Content Side */}

           {/* Visual Side */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            style={{ scale: scaleParallax }}
            className="space-y-4 lg:space-y-6 order-2 lg:order-1"
          >
            <div 
              initial={{ opacity: 0, y: 50, rotateZ: -5 }}
              animate={isInView ? { opacity: 1, y: 0, rotateZ: -2 } : {}}
              style={{ y: yParallax, rotate: rotateParallax }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ rotateZ: 0, scale: 1.02 }}
              className="bg-gray-50 p-2 sm:p-3 lg:p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transform transition-transform duration-300"
            >
              <h3 className="text-base lg:text-2xl font-black font-[var(--font-anton)] text-foreground mb-2 lg:mb-4">
                Vision
              </h3>
              <p className="font-[var(--font-dm-sans)] text-muted-foreground leading-relaxed text-xs lg:text-base">
Machine learning is one of the most powerful tools shaping our future. At Dalhousie Machine Learning Society, we're building a community where students grow together in this field. Our vision is to create an environment where every student has the opportunity to develop their skills in machine learning, supported by peers who share their ambition and curiosity.
              </p>
            </div>
            
            <div 
              initial={{ opacity: 0, y: 50, rotateZ: 5 }}
              animate={isInView ? { opacity: 1, y: 0, rotateZ: 2 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              whileHover={{ rotateZ: 0, scale: 1.02 }}
              className="bg-[#0395fc] p-2 sm:p-3 lg:p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transform transition-transform duration-300"
            >
              <h3 className="text-base lg:text-2xl font-black font-[var(--font-anton)] text-white mb-2 lg:mb-4">
                Goals
              </h3>
              <p className="font-[var(--font-dm-sans)] text-white leading-relaxed text-xs lg:text-base">
We focus on workshops, projects, and research. Workshops teach essential ML concepts and techniques. Projects let you apply machine learning to real-world problems. Research connects you with meaningful questions in the field, helping you explore what matters to you.
              </p>
            </div>
            
            <div 
              initial={{ opacity: 0, y: 50, rotateZ: -3 }}
              animate={isInView ? { opacity: 1, y: 0, rotateZ: -1 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
              whileHover={{ rotateZ: 0, scale: 1.02 }}
              className="bg-gray-50 p-2 sm:p-3 lg:p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transform transition-transform duration-300"
            >
              <h3 className="text-base lg:text-2xl font-black font-[var(--font-anton)] text-foreground mb-2 lg:mb-4">
                Join Us
              </h3>
              <p className="font-[var(--font-dm-sans)] text-muted-foreground leading-relaxed text-xs lg:text-base">
              The Dalhousie Machine Learning Society brings together students who want to learn and build. Connect with a community that values growth. Work on problems that challenge you. Develop skills that open doors. This is where you take the next step in machine learning.
              </p>
            </div>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="space-y-6 lg:space-y-8 order-1 lg:order-2"
          >
            <div className="space-y-4 lg:space-y-6 flex flex-col items-center justify-center mx-auto">
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="flex items-center justify-start gap-2 px-4 py-2 bg-white rounded-full border-2 border-[#0395fc] shadow-sm w-fit mx-auto lg:mx-0"
              >
                <span className="font-bold text-sm font-[var(--font-dm-sans)]" style={{ color: '#0395fc' }}>Who We Are</span>
              </motion.div>
              
              <motion.h2 
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="text-3xl sm:text-4xl lg:text-5xl font-black font-[var(--font-anton)] leading-tight text-center lg:text-left"
              >
                <span className="block text-foreground">ABOUT</span>
                <span className="block" style={{ color: '#0395fc' }}>DMLS</span>
              </motion.h2>
              
              <motion.div 
                initial={{ opacity: 0, scaleX: 0 }}
                animate={isInView ? { opacity: 1, scaleX: 1 } : {}}
                transition={{ duration: 0.8, delay: 0.9 }}
                className="h-1.5 lg:h-2 w-20 lg:w-24 bg-[#0395fc] transform -skew-x-12 mx-auto lg:mx-0 origin-left"
              ></motion.div>
            </div>
            
            <div className="space-y-4 lg:space-y-6 font-[var(--font-dm-sans)] text-base lg:text-lg leading-relaxed text-muted-foreground text-center lg:text-left px-4 lg:px-0">
              <div className="bg-gray-50 p-2 sm:p-3 lg:p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transform transition-transform duration-300 space-y-4 lg:space-y-6">
                <p className="font-[var(--font-dm-sans)] text-muted-foreground leading-relaxed text-base lg:text-lg text-center lg:text-left">
                  The <strong className="text-[#0395fc]">Dalhousie Machine Learning Society</strong> is a student-led organization at Dalhousie University, operating under the Dalhousie Student Union (DSU). We are dedicated to fostering community, collaboration, and hands-on learning in the rapidly evolving fields of machine learning and artificial intelligence.
                </p>
                <p className="font-[var(--font-dm-sans)] text-muted-foreground leading-relaxed text-base lg:text-lg text-center lg:text-left">
                 We believe that machine learning is one of the most powerful forces shaping our future, and our vision is to build a supportive community where students grow together developing their skills, exploring new ideas, and sharing a curiosity for AI. DMLS strives to create an environment where every student, regardless of background or experience, has the opportunity to learn and contribute.
                </p>
                <p className="font-[var(--font-dm-sans)] text-muted-foreground leading-relaxed text-base lg:text-lg text-center lg:text-left">
                  Our initiatives reflect this mission.
We host workshops that build essential ML foundations, offer projects that allow students to apply machine learning to  practical challenges, and support research efforts that encourage deeper exploration into meaningful problems.
                </p>
              </div>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 lg:gap-6 pt-6 lg:pt-8 px-4 lg:px-0">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-black font-[var(--font-anton)]" style={{ color: '#0395fc' }}>
                  200+
                </div>
                <div className="text-xs sm:text-sm font-bold font-[var(--font-dm-sans)] text-muted-foreground uppercase tracking-wider">
                  Students Reached
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-black font-[var(--font-anton)]" style={{ color: '#0395fc' }}>
                  5
                </div>
                <div className="text-xs sm:text-sm font-bold font-[var(--font-dm-sans)] text-muted-foreground uppercase tracking-wider">
                  Events
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-black font-[var(--font-anton)]" style={{ color: '#0395fc' }}>
                  1
                </div>
                <div className="text-xs sm:text-sm font-bold font-[var(--font-dm-sans)] text-muted-foreground uppercase tracking-wider">
                  Year Established
                </div>
              </div>
            </div>
          </motion.div>
          
        
        </div>
      </div>
    </section>
  );
};

export { AboutUsSection };

// Code Example Section Component
const CodeExampleSection = () => {
  const [selectedLanguage, setSelectedLanguage] = useState('python');
  const [copiedCode, setCopiedCode] = useState(false);
  
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Custom Button Component
  const Button = ({ children, onClick, variant = "primary", size = "md", className = "" }) => {
    const baseClasses = "border-2 border-black font-bold transition-all duration-200 inline-flex items-center justify-center";
    const variants = {
      primary: "bg-primary text-white hover:bg-primary/90 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px]",
      outline: "bg-white text-black hover:bg-gray-50 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px]"
    };
    const sizes = {
      sm: "px-3 py-1 text-sm",
      md: "px-4 py-2",
      lg: "px-6 py-3 text-lg"
    };
    
    return (
      <button 
        onClick={onClick}
        className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      >
        {children}
      </button>
    );
  };

  // Custom Tabs Components
  const Tabs = ({ children, value, onValueChange, className = "" }) => {
    return (
      <div className={className} data-value={value}>
        {React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, { currentValue: value, onValueChange });
          }
          return child;
        })}
      </div>
    );
  };

  const TabsList = ({ children, className = "", currentValue, onValueChange }) => {
    return (
      <div className={`bg-gray-100 border-2 border-black p-1 ${className}`}>
        {React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, { currentValue, onValueChange });
          }
          return child;
        })}
      </div>
    );
  };

  const TabsTrigger = ({ children, value, currentValue, onValueChange }) => {
    const isSelected = currentValue === value;
    return (
      <button
        onClick={() => onValueChange && onValueChange(value)}
        className={`px-3 py-2 text-sm font-bold border-2 border-black transition-all duration-200 ${
          isSelected
            ? 'bg-[#0395fc] text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] border-[#0395fc]'
            : 'bg-white text-black hover:bg-gray-50'
        }`}
      >
        {children}
      </button>
    );
  };

  const TabsContent = ({ children, value, className = "", currentValue }) => {
    if (currentValue !== value) return null;
    return <div className={className}>{children}</div>;
  };
  
  const mlCodeExamples = {
    python: {
      language: "python",
      filename: "neural_network.py",
      code: `import tensorflow as tf
from tensorflow import keras
import numpy as np

# Build a simple neural network
model = keras.Sequential([
    keras.layers.Dense(128, activation='relu', input_shape=(784,)),
    keras.layers.Dropout(0.2),
    keras.layers.Dense(10, activation='softmax')
])

# Compile the model
model.compile(
    optimizer='adam',
    loss='sparse_categorical_crossentropy',
    metrics=['accuracy']
)

# Load and preprocess MNIST data
(x_train, y_train), (x_test, y_test) = keras.datasets.mnist.load_data()
x_train = x_train.reshape(60000, 784).astype('float32') / 255
x_test = x_test.reshape(10000, 784).astype('float32') / 255

# Train the model
model.fit(x_train, y_train, epochs=5, batch_size=32)

# Evaluate performance
test_loss, test_acc = model.evaluate(x_test, y_test)
print(f'Test accuracy: {test_acc:.4f}')`
    },
    javascript: {
      language: "javascript",
      filename: "ml_browser.js",
      code: `import * as tf from '@tensorflow/tfjs';

// Create a neural network model
const model = tf.sequential({
  layers: [
    tf.layers.dense({ inputShape: [4], units: 10, activation: 'relu' }),
    tf.layers.dense({ units: 10, activation: 'relu' }),
    tf.layers.dense({ units: 3, activation: 'softmax' })
  ]
});

// Compile the model
model.compile({
  optimizer: 'adam',
  loss: 'categoricalCrossentropy',
  metrics: ['accuracy']
});

// Generate sample data
const xs = tf.randomNormal([100, 4]);
const ys = tf.oneHot(tf.randomUniform([100], 0, 3, 'int32'), 3);

// Train the model
async function trainModel() {
  const history = await model.fit(xs, ys, {
    epochs: 50,
    batchSize: 32,
    validationSplit: 0.2,
    callbacks: {
      onEpochEnd: (epoch, logs) => {
        console.log(\`Epoch \${epoch}: loss = \${logs.loss.toFixed(4)}\`);
      }
    }
  });
  
  console.log('Training complete!');
  return history;
}

trainModel();`
    },
    sql: {
      language: "sql",
      filename: "ml_queries.sql",
      code: `-- Create a machine learning model in BigQuery
CREATE OR REPLACE MODEL housing.price_model
OPTIONS(
  model_type='LINEAR_REG',
  input_label_cols=['price']
) AS
SELECT
  bedrooms, bathrooms, sqft_living,
  floors, waterfront, grade, price
FROM housing.training_data
WHERE price IS NOT NULL;

-- Evaluate model performance
SELECT
  mean_absolute_error,
  mean_squared_error,
  r2_score
FROM ML.EVALUATE(MODEL housing.price_model,
  (SELECT * FROM housing.test_data));

-- Make predictions on new data
SELECT 
  predicted_price,
  actual_price,
  ABS(predicted_price - actual_price) as error
FROM ML.PREDICT(MODEL housing.price_model,
  (SELECT * FROM housing.new_data))
ORDER BY error DESC
LIMIT 10;`
    },
    r: {
      language: "r",
      filename: "statistical_ml.R",
      code: `# Load required libraries
library(randomForest)
library(caret)
library(ggplot2)
library(dplyr)

# Load and explore data
data <- read.csv('dataset.csv')
str(data)
summary(data)

# Data preprocessing
data_clean <- data %>%
  na.omit() %>%
  mutate(
    target = as.factor(target),
    feature_1 = scale(feature_1),
    feature_2 = scale(feature_2)
  )

# Split data into training and testing sets
set.seed(123)
trainIndex <- createDataPartition(data_clean$target,
                                p = 0.8, list = FALSE)
train_data <- data_clean[trainIndex, ]
test_data <- data_clean[-trainIndex, ]

# Train Random Forest model
rf_model <- randomForest(
  target ~ .,
  data = train_data,
  ntree = 500,
  importance = TRUE
)

# Make predictions and evaluate
predictions <- predict(rf_model, test_data)
confusionMatrix(predictions, test_data$target)

# Feature importance plot
varImpPlot(rf_model)`
    }
  };

  return (
    <section className="py-16 lg:py-24 bg-transparent relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="grid items-center gap-12 lg:gap-16 lg:grid-cols-2">
          {/* Right Content - Code Examples */}
          <div className="relative order-2 lg:order-2 max-w-[90%] sm:max-w-xl lg:max-w-2xl mx-auto lg:mx-0">
            <div className="bg-white border-2 sm:border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transform -rotate-1 hover:rotate-0 transition-transform duration-300">
              <div className="p-1.5 sm:p-3 lg:p-5">
                <div className="mb-2 sm:mb-4 lg:mb-6">
                  <h3 className="text-sm sm:text-base lg:text-xl font-black text-foreground flex items-center gap-2 mb-2 lg:mb-4">
                    <Terminal className="size-3 sm:size-4 lg:size-5 text-primary" />
                    Practice with us
                  </h3>
                  
                  <Tabs value={selectedLanguage} onValueChange={setSelectedLanguage} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-4 lg:mb-6">
                      <TabsTrigger value="python">Python</TabsTrigger>
                      <TabsTrigger value="javascript">JS</TabsTrigger>
                      <TabsTrigger value="sql">SQL</TabsTrigger>
                      <TabsTrigger value="r">R</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="python" className="mt-0">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm sm:text-base lg:text-lg font-bold text-foreground">Neural Networks with TensorFlow</h4>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(mlCodeExamples.python.code)}
                            className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                          >
                            {copiedCode ? <Check className="size-4" /> : <Copy className="size-4" />}
                            {copiedCode ? 'Copied!' : 'Copy'}
                          </Button>
                        </div>
                        
                        <div className="bg-gray-800 rounded-none border-2 border-black overflow-hidden">
                          <div className="bg-gray-700 px-2 sm:px-4 py-1 sm:py-2 border-b border-gray-600">
                            <span className="text-white text-xs sm:text-sm font-mono">$ python neural_network.py</span>
                          </div>
                          <div className="p-2 sm:p-4 max-h-48 sm:max-h-64 lg:max-h-96 overflow-y-auto">
                            <pre className="text-green-400 text-xs sm:text-sm leading-relaxed font-mono whitespace-pre-wrap">
                              {mlCodeExamples.python.code}
                            </pre>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="javascript" className="mt-0">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-lg font-bold text-foreground">ML with TensorFlow.js</h4>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(mlCodeExamples.javascript.code)}
                            className="flex items-center gap-2 text-sm"
                          >
                            {copiedCode ? <Check className="size-4" /> : <Copy className="size-4" />}
                            {copiedCode ? 'Copied!' : 'Copy'}
                          </Button>
                        </div>
                        
                        <div className="bg-gray-800 rounded-none border-2 border-black overflow-hidden">
                          <div className="bg-gray-700 px-2 sm:px-4 py-1 sm:py-2 border-b border-gray-600">
                            <span className="text-white text-xs sm:text-sm font-mono">$ node ml_browser.js</span>
                          </div>
                          <div className="p-2 sm:p-4 max-h-48 sm:max-h-64 lg:max-h-96 overflow-y-auto">
                            <pre className="text-green-400 text-xs sm:text-sm leading-relaxed font-mono whitespace-pre-wrap">
                              {mlCodeExamples.javascript.code}
                            </pre>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="sql" className="mt-0">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-lg font-bold text-foreground">ML with SQL</h4>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(mlCodeExamples.sql.code)}
                            className="flex items-center gap-2 text-sm"
                          >
                            {copiedCode ? <Check className="size-4" /> : <Copy className="size-4" />}
                            {copiedCode ? 'Copied!' : 'Copy'}
                          </Button>
                        </div>
                        
                        <div className="bg-gray-800 rounded-none border-2 border-black overflow-hidden">
                          <div className="bg-gray-700 px-2 sm:px-4 py-1 sm:py-2 border-b border-gray-600">
                            <span className="text-white text-xs sm:text-sm font-mono">$ bq query --use_legacy_sql=false</span>
                          </div>
                          <div className="p-2 sm:p-4 max-h-48 sm:max-h-64 lg:max-h-96 overflow-y-auto">
                            <pre className="text-green-400 text-xs sm:text-sm leading-relaxed font-mono whitespace-pre-wrap">
                              {mlCodeExamples.sql.code}
                            </pre>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="r" className="mt-0">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-lg font-bold text-foreground">Statistical ML with R</h4>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(mlCodeExamples.r.code)}
                            className="flex items-center gap-2 text-sm"
                          >
                            {copiedCode ? <Check className="size-4" /> : <Copy className="size-4" />}
                            {copiedCode ? 'Copied!' : 'Copy'}
                          </Button>
                        </div>
                        
                        <div className="bg-gray-800 rounded-none border-2 border-black overflow-hidden">
                          <div className="bg-gray-700 px-2 sm:px-4 py-1 sm:py-2 border-b border-gray-600">
                            <span className="text-white text-xs sm:text-sm font-mono">$ Rscript statistical_ml.R</span>
                          </div>
                          <div className="p-2 sm:p-4 max-h-48 sm:max-h-64 lg:max-h-96 overflow-y-auto">
                            <pre className="text-green-400 text-xs sm:text-sm leading-relaxed font-mono whitespace-pre-wrap">
                              {mlCodeExamples.r.code}
                            </pre>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute -top-6 -right-6 w-12 h-12 bg-[#0395fc] border-2 border-black transform rotate-12 flex items-center justify-center shadow-md">
              <Code className="size-6 text-white" />
            </div>
            <div className="absolute -bottom-6 -left-6 w-8 h-8 bg-[#0395fc] border-2 border-black transform -rotate-12 shadow-md"></div>
            
            {/* Background Elements */}
            <div className="absolute -z-10 top-4 left-4 w-full h-full bg-[#0395fc]/10 border-2 border-black transform rotate-1"></div>
          </div>
          
          {/* Left Content */}
          <div className="space-y-6 lg:space-y-8 order-1 lg:order-1">
            <div className="space-y-4">
              
              <h2 className="text-3xl sm:text-4xl lg:text-6xl font-black font-[var(--font-anton)] leading-tight">
                <span className="block text-foreground">LEARN</span>
                <span className="block" style={{ color: '#0395fc' }}>WHAT</span>
                <span className="block text-accent">MATTERS</span>
              </h2>
              
              <div className="h-1.5 lg:h-2 w-20 lg:w-24 bg-[#0395fc] transform -skew-x-12"></div>
            </div>
            
            <p className="text-base lg:text-xl text-muted-foreground font-[var(--font-dm-sans)] leading-relaxed">
Get Hands-on experience with industry standard machine learning framework. From neural networks to computer vision, explore the concepts and tools of AI.
            </p>
            
            <div className="bg-gray-50 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] p-4 sm:p-6 lg:p-8 space-y-4 lg:space-y-6 flex flex-col items-center justify-center mx-auto px-4 text-center transform -rotate-1 hover:rotate-0 transition-transform duration-300">
              <div className="flex items-start gap-3 lg:gap-4">
                <div className="bg-[#0395fc] border-2 border-black h-6 w-6 lg:h-8 lg:w-8 flex items-center justify-center transform -rotate-3 shadow-sm flex-shrink-0 mt-1">
                  <Brain className="size-3 lg:size-4 text-white" />
                </div>
                <div>
                  <h3 className="font-black text-base lg:text-lg mb-1 lg:mb-2">Collaborate and Create</h3>
                  <p className="text-muted-foreground text-sm lg:text-base">Brainstorm, build, and grow with like minded peers.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 lg:gap-4">
                <div className="bg-accent border-2 border-black h-6 w-6 lg:h-8 lg:w-8 flex items-center justify-center transform rotate-3 shadow-sm flex-shrink-0 mt-1">
                  <Code className="size-3 lg:size-4 text-black" />
                </div>
                <div>
                  <h3 className="font-black text-base lg:text-lg mb-1 lg:mb-2">Industry-Driven Skills</h3>
                  <p className="text-muted-foreground text-sm lg:text-base">Learn tools and languages used by professionals worldwide.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 lg:gap-4">
                <div className="bg-secondary border-2 border-black h-6 w-6 lg:h-8 lg:w-8 flex items-center justify-center transform -rotate-3 shadow-sm flex-shrink-0 mt-1">
                  <Network className="size-3 lg:size-4 text-black" />
                </div>
                <div>
                  <h3 className="font-black text-base lg:text-lg mb-1 lg:mb-2">Impactful Projects</h3>
                  <p className="text-muted-foreground text-sm lg:text-base">Apply your knowledge to solve real-world challenges</p>
                </div>
              </div>
            </div>
            
          </div>
          
          
        </div>
      </div>
    </section>
  );
};

export { CodeExampleSection };

// Animated Banner Component
const WhyJoinBanner = () => {
  const bannerRef = useRef(null);
  const isInView = useInView(bannerRef, { once: true, threshold: 0.3 });

  return (
    <section ref={bannerRef} className="py-16 lg:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="relative bg-[#eaf6fe] border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-8 md:p-12 lg:p-16"
        >
          {/* Background decoration */}
          <motion.div 
            className="absolute -z-10 top-4 left-4 w-full h-full bg-[#0395fc]/10 border-2 border-black transform rotate-1"
          />
          <motion.div 
            className="absolute -z-20 top-8 left-8 w-full h-full bg-[#0395fc]/5 border-2 border-black transform -rotate-1"
          />
          
          {/* Content */}
          <div className="relative z-10 text-center max-w-4xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, scale: 0.5 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-[#0395fc] text-white px-4 py-2 rounded-full text-sm font-bold mb-6 lg:mb-8"
            >
              Join Us Today
            </motion.div>

            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black font-[var(--font-anton)] text-foreground mb-4 lg:mb-6 leading-tight"
            >
              WHY JOIN{" "}
              <motion.span
                className="inline-block text-[#0395fc]"
                animate={{ 
                  scale: [1, 1.05, 1],
                  textShadow: [
                    "0 0 0px rgba(3,149,252,0)",
                    "0 0 15px rgba(3,149,252,0.3)",
                    "0 0 0px rgba(3,149,252,0)"
                  ]
                }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                DMLS?
              </motion.span>
            </motion.h2>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto font-[var(--font-dm-sans)] leading-relaxed"
            >
              Join DMLS to connect with fellow learners, collaborate on projects, and build skills for a future in AI and ML.
            </motion.p>

            {/* Decorative corner elements */}
            <div className="absolute -top-6 -right-6 w-12 h-12 bg-[#0395fc] border-2 border-black transform rotate-12 flex items-center justify-center shadow-md">
              <Users className="size-6 text-white" />
            </div>
            <div className="absolute -bottom-4 -left-4 w-8 h-8 bg-accent border-2 border-black transform -rotate-12 shadow-md" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const InspireSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  // Parallax transforms for shooting stars and background elements
  const yStars = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const yStarsFast = useTransform(scrollYProgress, [0, 1], ["0%", "150%"]);
  
  const collaborationFeatures = [
    {
      icon: Brain,
      color: 'bg-[#0395fc]',
      title: 'Research Projects',
      description: 'Be a part of actual research sourced from researchers and contribute to the research community.'
    },
    {
      icon: Building2,
      color: 'bg-accent',
      title: 'Industry Projects',
      description: 'Learn from industrial challenges by being a part of real-world projects and gain practical experience.'
    },
    {
      icon: Calendar,
      color: 'bg-secondary',
      title: 'Events & Workshops',
      description: 'Attend our events to learn trending AI/ML concepts, create valuable projects, and earn certificates.'
    },
    {
      icon: Users,
      color: 'bg-[#0395fc]',
      title: 'Mentorship Program',
      description: 'Really interested in AI/ML but don\'t know where to start? DMLS has will help you navigate your learning journey.'
    }
  ];

  return (
    <section ref={ref} className="py-24 bg-transparent relative overflow-hidden">
      {/* Enhanced Shooting Stars Background with Parallax */}
      <motion.div 
        style={{ y: yStars }}
        className="absolute inset-0 pointer-events-none"
      >
        {/* First shooting star */}
        <motion.div
          className="absolute w-1 h-1 bg-white rounded-full"
          style={{ y: yStarsFast }}
          initial={{ x: -100, y: 100, opacity: 0 }}
          animate={{
            x: [0, 800],
            y: [0, -400],
            opacity: [0, 1, 1, 0],
            scale: [0, 1, 1, 0]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: 3,
            ease: "easeOut"
          }}
        >
          {/* Tail effect */}
          <motion.div
            className="absolute -left-8 top-0 w-8 h-0.5 bg-gradient-to-r from-transparent to-[#0395fc] opacity-60"
            animate={{ scaleX: [0, 1, 0] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatDelay: 3,
              ease: "easeOut"
            }}
          />
        </motion.div>

        {/* Second shooting star */}
        <motion.div
          className="absolute w-1 h-1 bg-white rounded-full"
          initial={{ x: -50, y: 200, opacity: 0 }}
          animate={{
            x: [0, 600],
            y: [0, -300],
            opacity: [0, 1, 1, 0],
            scale: [0, 1.2, 1.2, 0]
          }}
          transition={{
            duration: 1.8,
            repeat: Infinity,
            repeatDelay: 4,
            delay: 1,
            ease: "easeOut"
          }}
        >
          <motion.div
            className="absolute -left-6 top-0 w-6 h-0.5 bg-gradient-to-r from-transparent to-blue-400 opacity-50"
            animate={{ scaleX: [0, 1, 0] }}
            transition={{
              duration: 1.8,
              repeat: Infinity,
              repeatDelay: 4,
              delay: 1,
              ease: "easeOut"
            }}
          />
        </motion.div>

        {/* Third shooting star */}
        <motion.div
          className="absolute w-1 h-1 bg-yellow-400 rounded-full"
          initial={{ x: -80, y: 50, opacity: 0 }}
          animate={{
            x: [0, 700],
            y: [0, -350],
            opacity: [0, 1, 1, 0],
            scale: [0, 0.8, 0.8, 0]
          }}
          transition={{
            duration: 2.2,
            repeat: Infinity,
            repeatDelay: 5,
            delay: 2.5,
            ease: "easeOut"
          }}
        >
          <motion.div
            className="absolute -left-5 top-0 w-5 h-0.5 bg-gradient-to-r from-transparent to-yellow-400 opacity-40"
            animate={{ scaleX: [0, 1, 0] }}
            transition={{
              duration: 2.2,
              repeat: Infinity,
              repeatDelay: 5,
              delay: 2.5,
              ease: "easeOut"
            }}
          />
        </motion.div>

        {/* Fourth shooting star - smaller and faster */}
        <motion.div
          className="absolute w-0.5 h-0.5 bg-cyan-300 rounded-full"
          initial={{ x: -30, y: 300, opacity: 0 }}
          animate={{
            x: [0, 500],
            y: [0, -250],
            opacity: [0, 1, 1, 0],
            scale: [0, 1, 1, 0]
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            repeatDelay: 3.5,
            delay: 4,
            ease: "easeOut"
          }}
        >
          <motion.div
            className="absolute -left-4 top-0 w-4 h-0.5 bg-gradient-to-r from-transparent to-cyan-300 opacity-30"
            animate={{ scaleX: [0, 1, 0] }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatDelay: 3.5,
              delay: 4,
              ease: "easeOut"
            }}
          />
        </motion.div>

        {/* Static twinkling stars */}
        <motion.div
          className="absolute top-20 right-20 w-1 h-1 bg-white rounded-full"
          animate={{ opacity: [0.3, 1, 0.3], scale: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-40 left-1/4 w-0.5 h-0.5 bg-yellow-300 rounded-full"
          animate={{ opacity: [0.2, 0.8, 0.2], scale: [0.5, 1.2, 0.5] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
        <motion.div
          className="absolute bottom-40 right-1/3 w-0.5 h-0.5 bg-yellow-300 rounded-full"
          animate={{ opacity: [0.4, 1, 0.4], scale: [0.3, 1, 0.3] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
        />
        <motion.div
          className="absolute top-1/3 left-20 w-1 h-1 bg-cyan-200 rounded-full"
          animate={{ opacity: [0.1, 0.6, 0.1], scale: [0.4, 1.1, 0.4] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />

        {/* Floating particles */}
        <motion.div
          className="absolute top-32 right-1/4 w-0.5 h-0.5 bg-blue-400/60 rounded-full"
          animate={{ 
            y: [0, -20, 0], 
            x: [0, 10, 0],
            opacity: [0.6, 1, 0.6] 
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-32 left-1/3 w-1 h-1 bg-blue-400/80 rounded-full"
          animate={{ 
            y: [0, -30, 0], 
            x: [0, -15, 0],
            opacity: [0.4, 0.8, 0.4] 
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
        <motion.div
          className="absolute top-2/3 right-20 w-0.5 h-0.5 bg-yellow-200/50 rounded-full"
          animate={{ 
            y: [0, 25, 0], 
            x: [0, -8, 0],
            opacity: [0.5, 1, 0.5] 
          }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
      </motion.div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Left Content - Text */}
          <div className="space-y-6 lg:space-y-8 order-2 lg:order-2">
            <div className="space-y-4">
              
              <motion.h2 
                className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight font-[var(--font-anton)] relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <motion.span 
                  className="block text-foreground"
                  animate={{ textShadow: ["0 0 0px rgba(0,0,0,0)", "0 0 10px rgba(0,0,0,0.3)", "0 0 0px rgba(0,0,0,0)"] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                >
                  INSPIRE.
                </motion.span>
                <motion.span 
                  className="block text-muted-foreground"
                  animate={{ textShadow: ["0 0 0px rgba(0,0,0,0)", "0 0 8px rgba(0,0,0,0.4)", "0 0 0px rgba(0,0,0,0)"] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                >
                  CREATE.
                </motion.span>
                <motion.span 
                  className="block relative" 
                  style={{ color: '#0395fc' }}
                  animate={{ 
                    textShadow: ["0 0 0px rgba(3,149,252,0)", "0 0 15px rgba(3,149,252,0.6)", "0 0 0px rgba(3,149,252,0)"],
                    scale: [1, 1.02, 1]
                  }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                >
                  TOGETHER.
                  {/* Subtle sparkle effect */}
                  <motion.div
                    className="absolute -top-2 -right-2 w-2 h-2 bg-yellow-300 rounded-full"
                    animate={{ 
                      opacity: [0, 1, 0],
                      scale: [0, 1, 0],
                      rotate: [0, 180, 360]
                    }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 3 }}
                  />
                </motion.span>
              </motion.h2>
              
              <div className="h-1.5 lg:h-2 w-20 lg:w-24 bg-[#0395fc] transform -skew-x-12"></div>
              
              <p className="text-muted-foreground text-base lg:text-lg leading-relaxed font-[var(--font-dm-sans)]">
               We believe the best ideas emerge through connection. At DMLS, we bring students together to learn from one another and build meaningful experiences.
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid sm:grid-cols-2 gap-3 lg:gap-6">
              {collaborationFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="space-y-2 lg:space-y-3">
                    <div className={`${feature.color} border-2 border-black p-1.5 lg:p-2 w-fit shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transform hover:rotate-3 transition-transform duration-200`}>
                      <Icon className="w-4 h-4 lg:w-5 lg:h-5 text-black" />
                    </div>
                    <div>
                      <h3 className="font-black text-sm lg:text-lg mb-1 font-[var(--font-anton)] text-foreground">{feature.title}</h3>
                      <p className="text-muted-foreground text-xs lg:text-sm font-[var(--font-dm-sans)]">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 lg:gap-4">
              <a
                href="https://airtable.com/appbzvXorTGCwSsy9/pagnC4dhnbbUOqngV/form"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button variant="default" size="lg" className="bg-black hover:bg-black/90 text-white font-[var(--font-anton)] border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-200 text-xs lg:text-base">
                  Join Our Research / Project Initiatives
                  <ArrowRight className="w-3 h-3 lg:w-5 lg:h-5" />
                </Button>
              </a>
              
            </div>
          </div>

          {/* Right Content - Visual Card */}
          <div className="w-full max-w-xl md:max-w-2xl lg:max-w-none mx-auto order-1 lg:order-1">
            <div className="relative">
              {/* Main Card */}
              <div className="bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-4 md:p-6 lg:p-8 transform hover:rotate-1 transition-transform duration-300">
                {/* Large Lightbulb Icon */}
                <div className="flex justify-center mb-4 md:mb-6 lg:mb-8">
                  <div className="relative">
                    <div className="bg-accent border-2 border-black p-4 md:p-6 lg:p-8 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transform -rotate-3">
                      <Lightbulb className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 text-black" />
                    </div>
                    {/* Sparkle decorations */}
                    <div className="absolute -top-2 -right-2 md:-top-2.5 md:-right-2.5 lg:-top-3 lg:-right-3 w-3 h-3 md:w-4 md:h-4 lg:w-6 lg:h-6 bg-primary border-2 border-black rotate-45"></div>
                    <div className="absolute -bottom-2 -left-2 md:-bottom-2.5 md:-left-2.5 lg:-bottom-3 lg:-left-3 w-3 h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4 bg-secondary border-2 border-black rotate-45"></div>
                  </div>
                </div>

                {/* Quote Cards */}
                <div className="space-y-3 md:space-y-4 lg:space-y-5">
                  <div className="bg-muted border-2 border-black p-3 md:p-4 lg:p-5 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transform rotate-1">
                    <blockquote className="text-sm md:text-base lg:text-lg font-black text-foreground mb-2 md:mb-2.5 lg:mb-3 font-[var(--font-anton)]">
                      "It's my first research project, and I've learnt how to find biomarkers, and verify research ideas."
                    </blockquote>
                    <p className="text-xs md:text-sm lg:text-sm text-muted-foreground font-bold font-[var(--font-dm-sans)]">— Xiaohan Liu</p>
                  </div>
                  <div className="bg-muted border-2 border-black p-3 md:p-4 lg:p-5 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transform rotate-1">
                    <blockquote className="text-sm md:text-base lg:text-lg font-black text-foreground mb-2 md:mb-2.5 lg:mb-3 font-[var(--font-anton)]">
                      "I explored how ML derives insights from brain data and improved my ML skills."
                    </blockquote>
                    <p className="text-xs md:text-sm lg:text-sm text-muted-foreground font-bold font-[var(--font-dm-sans)]">— Nafisah Nubah</p>
                  </div>
                </div>
              </div>

              {/* Decorative background layers */}
              <div className="absolute -bottom-3 -right-3 md:-bottom-3.5 md:-right-3.5 lg:-bottom-4 lg:-right-4 w-full h-full bg-[#0395fc]/20 border-2 border-black -z-10 transform rotate-2"></div>
              <div className="absolute -bottom-6 -right-6 md:-bottom-7 md:-right-7 lg:-bottom-8 lg:-right-8 w-full h-full bg-[#0395fc]/10 border-2 border-black -z-20 transform -rotate-1"></div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

// Default export for the main component
const Home = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  // Multiple parallax layers with different speeds
  const yBackground = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const ySlow = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const yMedium = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const yFast = useTransform(scrollYProgress, [0, 1], ["0%", "80%"]);
  
  // Rotation and scale effects
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 360]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1, 1.1, 0.9]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.3, 0.8, 0.6, 0.2]);

  return (
    <div ref={containerRef} className="relative overflow-hidden">
      {/* Smooth scroll container for better performance */}
      <div className="relative z-10" style={{ 
        backfaceVisibility: 'hidden',
        perspective: '1000px',
        transform: 'translateZ(0)',
        willChange: 'transform'
      }}>
      {/* Enhanced Parallax Background Elements */}
      <motion.div className="fixed top-0 left-0 w-full h-full pointer-events-none z-0">
        {/* Large background shapes - Furthest layer */}
        <motion.div 
          style={{ y: yBackground, opacity, filter: "blur(4px)" }}
          className="absolute top-0 left-0 w-full h-full"
        >
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#0395fc]/5 rounded-full blur-3xl"></div>
          <div className="absolute top-3/4 right-1/4 w-[600px] h-[600px] bg-[#0395fc]/3 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-3/4 w-80 h-80 bg-[#0395fc]/4 rounded-full blur-3xl"></div>
        </motion.div>

        {/* Medium layer - floating geometric shapes */}
        <motion.div 
          style={{ y: yMedium, rotate, filter: "blur(2px)" }}
          className="absolute top-20 right-20 w-32 h-32 pointer-events-none"
        >
          <div className="w-full h-full bg-gradient-to-r from-[#0395fc]/20 to-transparent rounded-lg blur-sm transform rotate-45"></div>
        </motion.div>
        
        <motion.div 
          style={{ y: ySlow, scale, filter: "blur(1px)" }}
          className="absolute bottom-40 left-20 w-24 h-24 pointer-events-none"
        >
          <div className="w-full h-full bg-yellow-300/20 rounded-full blur-lg"></div>
        </motion.div>

        {/* Fast layer - small floating elements - Closest layer */}
        <motion.div 
          style={{ y: yFast }}
          className="absolute top-1/3 left-1/2 w-16 h-16 pointer-events-none"
        >
          <div className="w-full h-full bg-white/30 rounded-full blur-md"></div>
        </motion.div>

        <motion.div 
          style={{ y: yMedium, rotate: rotate }}
          className="absolute bottom-1/4 right-1/3 w-20 h-20 pointer-events-none"
        >
          <div className="w-full h-full bg-[#0395fc]/15 transform rotate-45 blur-sm"></div>
        </motion.div>

        {/* Subtle grid pattern */}
        <motion.div 
          style={{ 
            y: ySlow, 
            opacity,
            backgroundImage: `
              linear-gradient(rgba(3, 149, 252, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(3, 149, 252, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
          className="absolute inset-0 opacity-5 pointer-events-none"
        />

        {/* Floating Particles */}
        <motion.div 
          style={{ y: yFast, rotate }}
          className="absolute top-1/4 left-1/6 w-4 h-4 bg-[#0395fc]/30 rounded-full blur-sm pointer-events-none"
        />
        <motion.div 
          style={{ y: ySlow, x: useTransform(scrollYProgress, [0, 1], ["0%", "20%"]) }}
          className="absolute top-3/4 left-1/2 w-6 h-6 bg-yellow-300/20 rounded-full blur-md pointer-events-none"
        />
        <motion.div 
          style={{ y: yMedium, rotate: useTransform(scrollYProgress, [0, 1], [0, -180]) }}
          className="absolute top-1/2 right-1/4 w-3 h-3 bg-blue-400/40 rounded-full blur-sm pointer-events-none"
        />
        <motion.div 
          style={{ y: yFast, scale: useTransform(scrollYProgress, [0, 1], [1, 0.5]) }}
          className="absolute bottom-1/3 left-3/4 w-5 h-5 bg-[#0395fc]/25 rounded-full blur-md pointer-events-none"
        />
      </motion.div>

      <div className="relative z-10">
        <Navbar />
        <Hero34 />
        
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <AboutUsSection />
        </motion.div>

         <WhyJoinBanner />

         <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <CodeExampleSection />
        </motion.div>
 
        
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <InspireSection />
        </motion.div>
        
        {/* /**
        To be uncommented later, when DMLS grows and has more valuable connections.
         */}

        {/* <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <ConnectSection />
        </motion.div> */}
        
        {/* <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, margin: "-100px" }}
          style={{ 
            y: useTransform(scrollYProgress, [0.4, 0.6], ["20px", "-20px"]),
            scale: useTransform(scrollYProgress, [0.4, 0.5, 0.6], [0.95, 1, 0.95])
          }}
        >
          <Feature13 />
        </motion.div> */}
        
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, margin: "-100px" }}
          style={{ 
            y: useTransform(scrollYProgress, [0.7, 0.9], ["30px", "-30px"]),
            rotateX: useTransform(scrollYProgress, [0.7, 0.8, 0.9], [2, 0, -2])
          }}
        >
          <Team1 />
        </motion.div>
        
        <Footer2 />
      </div>
      </div>
    </div>
  );
};

export default Home;